/**
 * LASParser — Basic LAS/LAZ point cloud parser (LAS 1.2/1.3 common records)
 *
 * Supports reading header and X,Y,Z + classification for the first N points.
 * Used to feed real survey / LiDAR data into the Cesium 3D globe.
 *
 * For full LAZ (compressed) support in production use 'las-js' or a worker + pako.
 * This is a solid starting point that works with uncompressed .las files.
 */

export interface LASPoint {
  x: number;
  y: number;
  z: number;
  classification: number;
  intensity?: number;
}

export interface LASHeader {
  pointCount: number;
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  minZ: number;
  maxZ: number;
  scale: [number, number, number];
  offset: [number, number, number];
}

export class LASParser {
  async parseLAS(
    arrayBuffer: ArrayBuffer,
    maxPoints = 20000
  ): Promise<{ header: LASHeader; points: LASPoint[] }> {
    if (!arrayBuffer || arrayBuffer.byteLength < 100) {
      throw new Error('Invalid or too small LAS file');
    }
    const view = new DataView(arrayBuffer);

    try {
      // LAS header (simplified) - with guards
      const pointDataOffset = view.getUint32(96, true) || 0;
      const pointDataFormat = view.getUint8(104);
      const pointDataRecordLength = view.getUint16(105, true) || 20;
      const numPointsLegacy = view.getUint32(107, true);
      const numPoints = numPointsLegacy || Number(view.getBigUint64(247, true) || 0);

      const scaleX = view.getDouble(131, true) || 1;
      const scaleY = view.getDouble(139, true) || 1;
      const scaleZ = view.getDouble(147, true) || 1;
      const offsetX = view.getDouble(155, true) || 0;
      const offsetY = view.getDouble(163, true) || 0;
      const offsetZ = view.getDouble(171, true) || 0;

      const minX = view.getDouble(187, true) || 0;
      const maxX = view.getDouble(179, true) || 0;
      const minY = view.getDouble(203, true) || 0;
      const maxY = view.getDouble(195, true) || 0;
      const minZ = view.getDouble(219, true) || 0;
      const maxZ = view.getDouble(211, true) || 0;

      const header: LASHeader = {
        pointCount: Number(numPoints) || 0,
        minX,
        maxX,
        minY,
        maxY,
        minZ,
        maxZ,
        scale: [scaleX, scaleY, scaleZ],
        offset: [offsetX, offsetY, offsetZ],
      };

      const points: LASPoint[] = [];
      let offset = pointDataOffset;

      const recordLen = pointDataRecordLength || 20;
      const count = Math.min(Number(numPoints) || 0, maxPoints);

      for (let i = 0; i < count; i++) {
        if (offset + recordLen > arrayBuffer.byteLength) break;
        const x = view.getInt32(offset, true) * scaleX + offsetX;
        const y = view.getInt32(offset + 4, true) * scaleY + offsetY;
        const z = view.getInt32(offset + 8, true) * scaleZ + offsetZ;

        let classification = 0;
        if (pointDataFormat < 6) {
          classification = view.getUint8(offset + 15) || 0;
        } else {
          classification = view.getUint8(offset + 16) & 0b11111;
        }

        points.push({ x, y, z, classification });
        offset += recordLen;
      }

      return { header, points };
    } catch (e) {
      console.error('LAS parse robust error:', e);
      throw new Error('Failed to parse LAS file robustly. Try a standard uncompressed .las.');
    }
  }
}

export const lasParser = new LASParser();
